/**
 * 사용자가 생성기 입력창을 연 표면 — 진입 카드가 aiGenerateDockOpenPlaceAtom 에 남긴 표면을 입력창이 열린 순간
 * 소비해 quiz_ai_generator_dock_open 을 1회 남기고, 닫기·실행 계측이 이어받도록 기억해 돌려준다.
 * 실행 결과로 다시 연 입력창은 표면이 없어 남기지 않는다.
 */
import { useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

import { trackEvent } from '@zep/logger/tracking';

import { type AiGenerateDockSourceType } from '../consts/aiGenerateDock.const';
import {
  aiGenerateDockOpenPlaceAtom,
  aiGenerateDockUiAtom,
} from '../stores/aiGenerateDock.atoms';
import { type AiGenerateDockGate } from '../utils/aiGenerateDockGate';
import { toAiGeneratorGate } from '../utils/aiGenerateDockTracking';
import { type AiGeneratorOpenedPlace } from '../utils/events.quiz-ai-generator';

export const useAiGenerateDockOpenPlace = ({
  open,
  gate,
  sourceType,
  remainingSlots,
  quizCount,
  usageRemaining,
}: {
  /** 입력창이 보이나 */
  open: boolean;
  gate: AiGenerateDockGate;
  sourceType: AiGenerateDockSourceType;
  remainingSlots: number;
  quizCount: number;
  /** 무료 체험 남은 횟수 — 모르면 null. */
  usageRemaining: number | null;
}): AiGeneratorOpenedPlace => {
  const openPlace = useAtomValue(aiGenerateDockOpenPlaceAtom);
  const dispatchUi = useSetAtom(aiGenerateDockUiAtom);
  const [openedPlace, setOpenedPlace] = useState<AiGeneratorOpenedPlace>('');
  // 사용자가 닫혀 있던 입력창을 연 순간 1회 — 그 순간 실제로 걸린 게이트를 함께 남긴다.
  useEffect(() => {
    if (!open || !openPlace) return;
    dispatchUi({ type: 'consumeOpenPlace' });
    setOpenedPlace(openPlace);
    // place 는 리터럴로 적는다 — 퍼널 출처를 발화 지점에서 정적으로 감사할 수 있어야 한다.
    trackEvent('quiz_ai_generator_dock_open', {
      place: 'entry_card',
      source_type: sourceType,
      gate: toAiGeneratorGate(gate.kind),
      remaining_slot_count: remainingSlots,
      quiz_count: quizCount,
    });
    // 무료 소진 배너가 걸린 입력창 — 채팅과 같은 업셀 퍼널의 시작점이다.
    if (gate.kind === 'quotaExhausted') {
      trackEvent('quiz_ai_builder_usage_limit_view', {
        place: 'generator_dock',
        remaining: usageRemaining ?? 0,
        limit: -1,
      });
    }
  }, [
    gate.kind,
    open,
    openPlace,
    quizCount,
    remainingSlots,
    dispatchUi,
    sourceType,
    usageRemaining,
  ]);

  return openedPlace;
};
